import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';

interface AgencyInfo {
  name: string;
  address: string;
  city: string;
  wilaya?: string;
  contact_phone: string;
  contact_email: string;
  logo_url?: string;
  brand_color?: string;
}

interface PropertyInfo {
  reference?: string;
  property_category: string;
  address: string;
  city: string;
  wilaya: string;
  owner_name: string;
  owner_phone?: string;
}

interface Reservation {
  start_date: string;
  end_date: string;
  guest_name: string;
  nights_count: number;
  price_per_night: number;
  total_cost: number;
}

interface Expense {
  expense_date: string;
  description: string;
  amount: number;
}

interface ConciergerieMonthlyBulletinPDFProps {
  agency: AgencyInfo;
  property: PropertyInfo;
  reservations: Reservation[];
  expenses: Expense[];
  month: number;
  year: number;
}

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

export const ConciergerieMonthlyBulletinPDF: React.FC<ConciergerieMonthlyBulletinPDFProps> = ({
  agency,
  property,
  reservations,
  expenses,
  month,
  year
}) => {
  const brandColor = agency.brand_color || '#0F766E';

  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#FFFFFF',
      fontFamily: 'Helvetica',
      padding: 30,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 25,
      paddingBottom: 15,
      borderBottom: 2,
      borderBottomColor: brandColor,
    },
    headerLeft: {
      flexDirection: 'column',
      gap: 6,
    },
    logo: {
      width: 100,
      height: 50,
      objectFit: 'contain',
      marginBottom: 8,
    },
    agencyName: {
      fontSize: 16,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 4,
    },
    agencyAddress: {
      fontSize: 9,
      color: '#6B7280',
      lineHeight: 1.4,
    },
    agencyContact: {
      fontSize: 9,
      color: '#6B7280',
      lineHeight: 1.4,
    },
    headerRight: {
      alignItems: 'flex-end',
    },
    bulletinTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: brandColor,
      marginBottom: 6,
    },
    bulletinPeriod: {
      fontSize: 12,
      color: '#374151',
      fontWeight: 'bold',
    },
    bulletinDate: {
      fontSize: 9,
      color: '#9CA3AF',
      marginTop: 4,
    },
    propertySection: {
      backgroundColor: '#F9FAFB',
      padding: 15,
      borderRadius: 8,
      marginBottom: 20,
      border: 1,
      borderColor: '#E5E7EB',
    },
    propertySectionTitle: {
      fontSize: 11,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 10,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    propertyInfoRow: {
      flexDirection: 'row',
      gap: 15,
      marginBottom: 6,
    },
    propertyInfoColumn: {
      flex: 1,
    },
    propertyLabel: {
      fontSize: 8,
      color: '#6B7280',
      marginBottom: 2,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    propertyValue: {
      fontSize: 10,
      color: '#111827',
      fontWeight: 'bold',
    },
    tableSection: {
      marginBottom: 20,
    },
    tableSectionTitle: {
      fontSize: 13,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 10,
      paddingBottom: 5,
      borderBottom: 2,
    },
    reservationsSectionTitle: {
      borderBottomColor: '#0b8043',
    },
    expensesSectionTitle: {
      borderBottomColor: '#DC2626',
    },
    table: {
      border: 1,
      borderColor: '#E5E7EB',
      borderRadius: 6,
      overflow: 'hidden',
    },
    tableHeader: {
      flexDirection: 'row',
      borderBottom: 1,
      borderBottomColor: '#E5E7EB',
      paddingVertical: 8,
      paddingHorizontal: 10,
    },
    reservationsTableHeader: {
      backgroundColor: '#0b804315',
    },
    expensesTableHeader: {
      backgroundColor: '#DC262615',
    },
    tableHeaderCell: {
      fontSize: 8,
      fontWeight: 'bold',
      color: brandColor,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    tableRow: {
      flexDirection: 'row',
      borderBottom: 1,
      borderBottomColor: '#F3F4F6',
      paddingVertical: 8,
      paddingHorizontal: 10,
    },
    tableCell: {
      fontSize: 9,
      color: '#374151',
    },
    tableCellBold: {
      fontSize: 9,
      color: '#111827',
      fontWeight: 'bold',
    },
    emptyTable: {
      padding: 15,
      alignItems: 'center',
    },
    emptyTableText: {
      fontSize: 10,
      color: '#9CA3AF',
      fontStyle: 'italic',
    },
    subtotalRow: {
      flexDirection: 'row',
      backgroundColor: '#F9FAFB',
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderTop: 2,
      borderTopColor: '#E5E7EB',
    },
    subtotalLabel: {
      flex: 1,
      fontSize: 11,
      fontWeight: 'bold',
      color: '#111827',
      textAlign: 'right',
      paddingRight: 10,
    },
    subtotalValue: {
      fontSize: 11,
      fontWeight: 'bold',
      color: brandColor,
      textAlign: 'right',
    },
    summarySection: {
      backgroundColor: `${brandColor}10`,
      padding: 20,
      borderRadius: 8,
      border: 2,
      borderColor: brandColor,
      marginTop: 20,
    },
    summaryTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: '#111827',
      marginBottom: 15,
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: 1,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 10,
      paddingHorizontal: 10,
    },
    summaryLabel: {
      fontSize: 11,
      color: '#374151',
      fontWeight: 'bold',
    },
    summaryValue: {
      fontSize: 11,
      color: '#111827',
      fontWeight: 'bold',
    },
    summaryDivider: {
      borderTop: 1,
      borderTopColor: brandColor,
      marginVertical: 12,
      marginHorizontal: 10,
    },
    summaryNetRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingHorizontal: 10,
      paddingVertical: 12,
      backgroundColor: brandColor,
      borderRadius: 6,
      marginTop: 5,
    },
    summaryNetLabel: {
      fontSize: 13,
      color: '#FFFFFF',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    summaryNetValue: {
      fontSize: 16,
      color: '#FFFFFF',
      fontWeight: 'bold',
    },
    footer: {
      position: 'absolute',
      bottom: 20,
      left: 30,
      right: 30,
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingTop: 10,
      borderTop: 1,
      borderTopColor: '#E5E7EB',
    },
    footerText: {
      fontSize: 8,
      color: '#9CA3AF',
    },
    reservationDateCol: { width: '30%' },
    reservationClientCol: { width: '30%' },
    reservationNightsCol: { width: '15%', textAlign: 'center' },
    reservationPriceCol: { width: '15%', textAlign: 'right' },
    reservationTotalCol: { width: '15%', textAlign: 'right' },
    expenseDateCol: { width: '20%' },
    expenseDescCol: { width: '65%' },
    expenseAmountCol: { width: '15%', textAlign: 'right' },
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const formatAmount = (amount: number) => {
    const rounded = Math.round(amount);
    const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${formatted} DA`;
  };

  const totalRevenue = reservations.reduce((sum, res) => sum + parseFloat(String(res.total_cost)), 0);
  const totalExpenses = expenses.reduce((sum, exp) => sum + parseFloat(String(exp.amount)), 0);
  const netAmount = totalRevenue - totalExpenses;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {agency.logo_url ? (
              <Image style={styles.logo} src={agency.logo_url} />
            ) : (
              <Text style={styles.agencyName}>{agency.name}</Text>
            )}
            <Text style={styles.agencyAddress}>
              {agency.address}
            </Text>
            <Text style={styles.agencyAddress}>
              {agency.city}{agency.wilaya ? `, ${agency.wilaya}` : ''}
            </Text>
            <Text style={styles.agencyContact}>
              Tél: {agency.contact_phone}
            </Text>
            <Text style={styles.agencyContact}>
              Email: {agency.contact_email}
            </Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.bulletinTitle}>BULLETIN MENSUEL</Text>
            <Text style={styles.bulletinPeriod}>{MONTHS[month - 1]} {year}</Text>
          </View>
        </View>

        <View style={styles.propertySection}>
          <Text style={styles.propertySectionTitle}>Informations du bien</Text>
          <View style={styles.propertyInfoRow}>
            <View style={styles.propertyInfoColumn}>
              <Text style={styles.propertyLabel}>Référence</Text>
              <Text style={styles.propertyValue}>{property.address}</Text>
            </View>
            <View style={styles.propertyInfoColumn}>
              <Text style={styles.propertyLabel}>Type de bien</Text>
              <Text style={styles.propertyValue}>{property.property_category}</Text>
            </View>
          </View>
          <View style={styles.propertyInfoRow}>
            <View style={styles.propertyInfoColumn}>
              <Text style={styles.propertyLabel}>Adresse</Text>
              <Text style={styles.propertyValue}>{property.address}</Text>
            </View>
            <View style={styles.propertyInfoColumn}>
              <Text style={styles.propertyLabel}>Ville</Text>
              <Text style={styles.propertyValue}>{property.city}, {property.wilaya}</Text>
            </View>
          </View>
          <View style={styles.propertyInfoRow}>
            <View style={styles.propertyInfoColumn}>
              <Text style={styles.propertyLabel}>Propriétaire</Text>
              <Text style={styles.propertyValue}>{property.owner_name}</Text>
            </View>
            <View style={styles.propertyInfoColumn}>
              <Text style={styles.propertyLabel}>Téléphone</Text>
              <Text style={styles.propertyValue}>{property.owner_phone || 'Non renseigné'}</Text>
            </View>
          </View>
        </View>

        <View style={styles.tableSection}>
          <Text style={[styles.tableSectionTitle, styles.reservationsSectionTitle]}>Réservations</Text>
          <View style={styles.table}>
            <View style={[styles.tableHeader, styles.reservationsTableHeader]}>
              <Text style={[styles.tableHeaderCell, styles.reservationDateCol, { color: '#0b8043' }]}>Période</Text>
              <Text style={[styles.tableHeaderCell, styles.reservationClientCol, { color: '#0b8043' }]}>Client</Text>
              <Text style={[styles.tableHeaderCell, styles.reservationNightsCol, { color: '#0b8043' }]}>Nuitées</Text>
              <Text style={[styles.tableHeaderCell, styles.reservationPriceCol, { color: '#0b8043' }]}>Prix/Nuit</Text>
              <Text style={[styles.tableHeaderCell, styles.reservationTotalCol, { color: '#0b8043' }]}>Total</Text>
            </View>
            {reservations.length > 0 ? (
              <>
                {reservations.map((reservation, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={[styles.tableCell, styles.reservationDateCol]}>
                      {formatDate(reservation.start_date)} - {formatDate(reservation.end_date)}
                    </Text>
                    <Text style={[styles.tableCell, styles.reservationClientCol]}>
                      {reservation.guest_name}
                    </Text>
                    <Text style={[styles.tableCellBold, styles.reservationNightsCol]}>
                      {reservation.nights_count}
                    </Text>
                    <Text style={[styles.tableCell, styles.reservationPriceCol]}>
                      {formatAmount(reservation.price_per_night)}
                    </Text>
                    <Text style={[styles.tableCellBold, styles.reservationTotalCol]}>
                      {formatAmount(reservation.total_cost)}
                    </Text>
                  </View>
                ))}
                <View style={styles.subtotalRow}>
                  <Text style={styles.subtotalLabel}>Total Revenus :</Text>
                  <Text style={[styles.subtotalValue, styles.reservationTotalCol, { color: '#0b8043' }]}>
                    {formatAmount(totalRevenue)}
                  </Text>
                </View>
              </>
            ) : (
              <View style={styles.emptyTable}>
                <Text style={styles.emptyTableText}>Aucune réservation ce mois-ci</Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.tableSection}>
          <Text style={[styles.tableSectionTitle, styles.expensesSectionTitle]}>Dépenses</Text>
          <View style={styles.table}>
            <View style={[styles.tableHeader, styles.expensesTableHeader]}>
              <Text style={[styles.tableHeaderCell, styles.expenseDateCol, { color: '#DC2626' }]}>Date</Text>
              <Text style={[styles.tableHeaderCell, styles.expenseDescCol, { color: '#DC2626' }]}>Description</Text>
              <Text style={[styles.tableHeaderCell, styles.expenseAmountCol, { color: '#DC2626' }]}>Montant</Text>
            </View>
            {expenses.length > 0 ? (
              <>
                {expenses.map((expense, index) => (
                  <View key={index} style={styles.tableRow}>
                    <Text style={[styles.tableCell, styles.expenseDateCol]}>
                      {formatDate(expense.expense_date)}
                    </Text>
                    <Text style={[styles.tableCell, styles.expenseDescCol]}>
                      {expense.description}
                    </Text>
                    <Text style={[styles.tableCellBold, styles.expenseAmountCol]}>
                      {formatAmount(expense.amount)}
                    </Text>
                  </View>
                ))}
                <View style={styles.subtotalRow}>
                  <Text style={styles.subtotalLabel}>Total Dépenses :</Text>
                  <Text style={[styles.subtotalValue, styles.expenseAmountCol, { color: '#DC2626' }]}>
                    {formatAmount(totalExpenses)}
                  </Text>
                </View>
              </>
            ) : (
              <View style={styles.emptyTable}>
                <Text style={styles.emptyTableText}>Aucune dépense ce mois-ci</Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.summarySection}>
          <View style={styles.summaryNetRow}>
            <Text style={styles.summaryNetLabel}>Montant Net :</Text>
            <Text style={styles.summaryNetValue}>{formatAmount(netAmount)}</Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>{agency.name}</Text>
          <Text style={styles.footerText}>
            Bulletin mensuel - {MONTHS[month - 1]} {year}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
